// src/utils/lookOnly.js
// "Look only" while the owner views the app as a specific PERSON (09/28/2026, Henry's call).
//
// View As a person swaps the signed-in user held in memory for a copy of that person's
// record (utils/roles.js applyViewAsPerson), so every screen shows what they see. The
// database and the server still know the real signed-in person (Henry), so anything
// saved in that view would be saved as HIM, stamped with the other person's name on
// screen. So in person view nothing may be saved: every shared save helper calls
// assertCanWrite() first, and every Firestore / Storage write goes through
// utils/lookOnlyFirestore.js, which asks the same question.
//
// The answer comes from one module-level copy of the signed-in user, kept current by the
// store (context/reducer.js calls setLookOnlyUser on every user change) and by the
// dashboard shell (components/ViewAsBanner.jsx). Viewing as a ROLE (no person) is not
// look only: those saves are Henry's own and were always allowed.
// Nothing here is saved or remembered on the device; a reload starts in his own view.

let currentUser = null;

export class LookOnlyError extends Error {
  constructor(message) {
    super(message);
    this.name = "LookOnlyError";
    this.lookOnly = true;
  }
}

// The name of the person being viewed as, "First Last", or a plain fallback.
export function lookOnlyName(user = currentUser) {
  const name = `${user?.firstname || ""} ${user?.lastname || ""}`.trim();
  return name || "another person";
}

export function lookOnlyMessage(user = currentUser) {
  return `You are viewing as ${lookOnlyName(user)}. Switch back to make changes.`;
}

// Is this user a person-view copy? Pure, for callers holding a user in hand.
export const isPersonView = (user) => Boolean(user && user.viewAsPersonId);

// Keep the module's copy of the signed-in user current. Called with every new user.
export function setLookOnlyUser(user) {
  currentUser = user && typeof user === "object" && !Array.isArray(user) ? user : null;
}

// True while the owner is viewing the app as a specific person.
export function isLookOnly() {
  return isPersonView(currentUser);
}

// Stops a save before it starts. Throws a LookOnlyError in person view; does nothing
// otherwise. Called at the top of every shared save helper.
export function assertCanWrite() {
  if (isLookOnly()) throw new LookOnlyError(lookOnlyMessage());
}

// The same refusal as a rejected promise, for helpers that are not async functions.
export function lookOnlyRejection() {
  return Promise.reject(new LookOnlyError(lookOnlyMessage()));
}

export const isLookOnlyError = (e) =>
  Boolean(e && (e instanceof LookOnlyError || e.lookOnly === true || e.name === "LookOnlyError"));

// The refusal as a plain result, for helpers that answer { ok, error } and never reject.
export function lookOnlyResult() {
  return { ok: false, lookOnly: true, error: lookOnlyMessage() };
}
